import React, {useState } from 'react'
import { UserDataGetUserDetailsQuery } from '@agtech/data/users/AgTechUserQueries';
import { AgTechQuery, AgTechQueryBuilder, AgTechQueryProp, AgTechQueryResponses, useAgTechQuery } from '@agtech/core/data/queries/AgTechDataQueries';
import { useAgTechWebAppContext } from '../Contexts/AgTechWebAppContext';
import { AgTechWebApplication } from '../../../application/AgTechWebApplication';
import { useAgTechAuthContext } from '@agtech/core/auth/AgTechAuthContext';
import { AgTechOperationApplicationStatus } from '@agtech/core/data/AgTechOperationData';
import AgTechWebAppOperationRegistrationModal from '../Registration/AgTechWebAppRegistrationModal';
import { AgTechErrorIcon } from '../../Icons/AgTechIcons';
import AgTechWebAppBackdrop from './AgTechWebAppBackdrop';
import { OperationDataGetOperationInfoQuery } from '@agtech/data/operations/OperationDataQueries';

const AgTechWebAppLoadingPage = (props: {
    app: AgTechWebApplication,
    children: JSX.Element
}) => {
    const appContext = useAgTechWebAppContext();
    const authContext = useAgTechAuthContext();

    const [loadingStatus, setLoadingStatus] = useState<'Loading' | 'Success' | 'Failed'>('Loading');

    const coreQueries = useCoreAgTechAppQueries(props.app);
    const appQueries = props.app.loading?.queries?.(authContext.user) ?? [];

    // If the app specifies a registraiton query, we will check the registration status
    // If not, we will assume the operation is fully provisioned and setup
    const registrationStatusQuery = props.app.registration?.registrationStatusQuery ?? {
        query: DefaultAppRegistrationQuery
    };

    const handleOperationRegistration = (registrationStatus: AgTechOperationApplicationStatus) => new Promise<void>((res) => {
        const requiresProvision = registrationStatus.isProvisioned === false;
        const requiresSetup = registrationStatus.isSetupComplete === false;

        if (requiresProvision || requiresSetup) {
            setTimeout(() => {
                appContext.modals.showModal({
                    height: '360px',
                    width: '800px',
                    content: () => (
                        <AgTechWebAppOperationRegistrationModal
                            app={props.app}
                            targetStep={requiresProvision ? 'intro' : 'setup'}
                            onCompleted={() => {
                                res();
                            }}
                        />
                    ),
                    showCloseButton: false
                })
            }, 2000);
        }
        else {
            res();
        }
    });

    return loadingStatus === 'Success' ? (
        <>
            {props.app.structure.dataContext ? props.app.structure.dataContext({
                app: props.app,
                children: props.children
            })
            : props.children}
        </>
    ) : (
        <AgTechWebAppBackdrop app={props.app}>
            <div className="stretched centered row-based container">
                {loadingStatus === 'Failed' ? (
                    <div className='row-based centered h-[100px] flex'>
                        <AgTechErrorIcon
                            size={36}
                            classes='!bg-red-500 text-white'
                        />
                        <h1 className='mt-5 text-lg font-semibold'>An error occurred. Please refresh the app.</h1>
                    </div>
                ) : (
                    <div className='row-based centered h-[100px] flex'>
                        <div
                            style={{ height: 32, width: 32 }}
                            className={`text-white border-[3px] border-white border-b-transparent border-r-transparent animate-spin rounded-full`}
                        />
                        <h1 className={`mt-5 text-lg font-semibold`}>Loading...</h1>
                    </div>
                )}
                <AgTechAppQueryLoader
                    queries={[
                        { 
                            query: registrationStatusQuery,
                            onQuerySuccess: handleOperationRegistration
                        },
                        ...coreQueries.map(q => ({
                            query: q,
                            onQuerySuccess: async () => {}
                        })),
                        ...appQueries.map(q => ({
                            query: q,
                            onQuerySuccess: async () => {}
                        }))
                    ]}
                    onAllQueriesSucceeded={() => {
                        setLoadingStatus('Success');
                    }}
                    onAnyQueriesFailed={() => setLoadingStatus('Failed')}
                />
            </div>
        </AgTechWebAppBackdrop>
    )
}

const DefaultAppRegistrationQuery: AgTechQuery<any, AgTechOperationApplicationStatus> = {
    queryKey: () => ['operation', 'registration', 'status'],
    query: async () => {
        return AgTechQueryResponses.Success({
            isProvisioned: true,
            isSetupComplete: true,
            termsOfServiceVersion: '1.0.0',
            acceptedTermsOfServiceVersion: '1.0.0'
        });
    },
    content: {
        loadingText: 'Checking operation registration status...',
        failureText: 'Failed to check operation registration status.'
    }
}

export const AgTechAppQueryLoader = React.memo((props: {
    queries: {
        query: AgTechQueryProp<any, any>,
        onQuerySuccess: (data: any) => Promise<void>
    }[],
    onAllQueriesSucceeded: () => void,
    onAnyQueriesFailed: () => void
}) => {    
    const [currentQueryIndex, setCurrentQueryIndex] = useState(0);

    const currentQuery = props.queries[currentQueryIndex] ?? AgTechQueryBuilder.buildQueryResult({} as any)
    
    const isLastQuery = currentQueryIndex === props.queries.length - 1;

    useAgTechQuery({
        ...currentQuery.query,
        settings: {
            eventHandlers: {
                onQuerySuccess: (data) => {
                    currentQuery.onQuerySuccess(data).then(() => {
                        if (isLastQuery) {
                            props.onAllQueriesSucceeded();
                        }
                        else {
                            setCurrentQueryIndex(curr => curr + 1);
                        }
                    });
                },
                onQueryFailed: () => {
                    props.onAnyQueriesFailed();
                }
            }
        }
    });

    return (
        <></>
    )
}, (prev, curr) => prev.queries.length === curr.queries.length);

export const useCoreAgTechAppQueries = (app: AgTechWebApplication): AgTechQueryProp<any, any>[] => {
    const currentUserId = useAgTechAuthContext().user.id;
    const appQueries: AgTechQueryProp<any, any>[] = [];

    if (currentUserId) {
        appQueries.push({ query: UserDataGetUserDetailsQuery, queryContext: { userId: currentUserId } });
        appQueries.push({ query: OperationDataGetOperationInfoQuery });
    }

    if (app.features) {
        appQueries.push({ query: app.features.query });
    }

    return appQueries;
}

const AgTechWebAppLoadingPageMemo = React.memo(AgTechWebAppLoadingPage);

export default AgTechWebAppLoadingPageMemo